import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Payment } from '@mercadopago/sdk-react';
import { auth, db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const RetryPaymentModal = ({ app, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [isBrickLoading, setIsBrickLoading] = useState(true);
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [isPixPaid, setIsPixPaid] = useState(false);
  const [dynamicPrice, setDynamicPrice] = useState(119.90);

  useEffect(() => {
    if (!Number(app.totalPrice)) {
      import('firebase/firestore').then(({ doc, getDoc }) => {
        const docRef = doc(db, 'settings', 'pricing');
        getDoc(docRef).then(docSnap => {
          if (docSnap.exists()) {
            setDynamicPrice(docSnap.data().base || 119.90);
          }
        }).catch(err => console.log('Erro ao buscar pricing:', err));
      });
    }
  }, [app.totalPrice]);

  // Fechar no ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showPixModal) {
          setShowPixModal(false);
          if (onSuccess) onSuccess();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
  }, [onClose, showPixModal, onSuccess]);

  useEffect(() => {
    let unsub = () => {};
    if (showPixModal && app?.id) {
      unsub = onSnapshot(doc(db, "apps", app.id), (docSnap) => {
        if (docSnap.exists()) {
          const appDoc = docSnap.data();
          if (appDoc.paymentStatus === 'approved') {
            setIsPixPaid(true);
            setTimeout(() => {
              setShowPixModal(false);
              if (onSuccess) onSuccess();
            }, 3000);
          }
        }
      });
    }
    return () => unsub();
  }, [showPixModal, app?.id, onSuccess]);

  useEffect(() => {
    const PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY || 'APP_USR-17381018-de8e-44a4-bfb7-e1303b6f6d19';
    import('@mercadopago/sdk-react').then(({ initMercadoPago }) => {
      initMercadoPago(PUBLIC_KEY, { locale: 'pt-BR' });
    }).catch(err => console.log('Erro ao carregar MP SDK', err));
  }, []);

  const handleCheckoutSubmit = async (paymentBrickResponse) => {
    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Usuário não autenticado');

      const actualPaymentData = paymentBrickResponse.formData || paymentBrickResponse;
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/processPaymentRetry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: app.id,
          paymentData: actualPaymentData,
          userAuth: {
            uid: user.uid,
            email: user.email
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaving(false);
        if (data.paymentMethodId === 'pix' && data.pix) {
          setPixData(data.pix);
          setShowPixModal(true);
        } else {
          if (data.ticketUrl) {
            // setTicketUrl(data.ticketUrl);
          }
          if (onSuccess) onSuccess();
        }
      } else {
        throw new Error('Falha ao processar pagamento.');
      }
    } catch (error) {
      console.error('Erro no checkout retry:', error);
      alert(`Erro: ${error.message}`);
      setSaving(false);
    }
  };

  const amountToPay = Number(app.totalPrice) || dynamicPrice;

  if (showPixModal && pixData) {
    return createPortal(
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm overflow-y-auto">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full mx-auto relative text-center max-h-[95vh] overflow-y-auto custom-scrollbar">
          <button 
            onClick={() => { setShowPixModal(false); if(onSuccess) onSuccess(); }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {isPixPaid ? (
            <div className="flex flex-col items-center justify-center py-8">
              <svg className="w-20 h-20 text-green-500 mb-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Pagamento Aprovado!</h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Redirecionando para o painel...
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pagamento via PIX</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">Escaneie o QR Code abaixo com o aplicativo do seu banco para finalizar a compra.</p>
              <div className="bg-gray-50 dark:bg-white p-4 rounded-xl inline-block border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
                <img 
                  src={`data:image/jpeg;base64,${pixData.qrCodeBase64}`} 
                  alt="QR Code PIX" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ou copie o código PIX copia e cola:</p>
                <div className="flex relative">
                  <input 
                    type="text" 
                    value={pixData.qrCode} 
                    readOnly 
                    className="w-full text-xs p-3 pr-12 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-400 focus:outline-none"
                  />
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(pixData.qrCode);
                      alert("Código PIX copiado!");
                    }}
                    className="absolute right-2 top-2 p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                    title="Copiar código PIX"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bg-blue-50 text-blue-800 p-4 rounded-lg text-sm mb-6 text-left border border-blue-100">
                <p className="flex items-start font-medium">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Aguardando confirmação do pagamento...
                </p>
                <p className="mt-1 text-blue-700 ml-7 text-xs">A tela atualizará automaticamente assim que o PIX for compensado.</p>
              </div>
              <button 
                onClick={() => { setShowPixModal(false); if(onSuccess) onSuccess(); }}
                className="w-full py-3 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors font-medium"
              >
                Já realizei o pagamento
              </button>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[100] p-0 sm:p-4 backdrop-blur-sm overflow-y-auto animate-fade-in">
      <div className="bg-white dark:bg-gray-800 p-5 sm:p-8 rounded-none sm:rounded-2xl shadow-2xl max-w-2xl w-full min-h-screen sm:min-h-[auto] max-h-screen sm:max-h-[90vh] flex flex-col mx-auto my-0 relative animate-slide-up">
        
        {/* HEADER DO MODAL - Fica Fixo */}
        <div className="flex-shrink-0 mb-6">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors z-10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white pr-8">Finalizar Compra</h3>
          <p className="text-gray-600 dark:text-gray-300">Você está pagando o app: <strong className="dark:text-white">{app.name}</strong><br/>Total a pagar: <strong className="text-gray-900 dark:text-white">R$ {amountToPay.toFixed(2)}</strong></p>
        </div>
        
        {saving && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 z-20 flex flex-col items-center justify-center rounded-none sm:rounded-2xl transition-all duration-300 backdrop-blur-sm">
             <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white mb-4"></div>
             <span className="font-semibold text-gray-800 dark:text-gray-200">Processando pagamento...</span>
          </div>
        )}

        {/* CORPO DO MODAL - Rola se for maior que a tela */}
        <div className="flex-1 overflow-y-auto custom-scrollbar relative pr-1 pb-4">
          <div className="min-h-[400px]">
          {isBrickLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-800 z-10 transition-opacity duration-300 rounded-xl">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 dark:border-gray-500 mb-4"></div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Carregando ambiente seguro...</span>
            </div>
          )}
          <div className={`transition-opacity duration-500 ${isBrickLoading ? 'opacity-0' : 'opacity-100'}`}>
            <Payment
            initialization={{ amount: amountToPay }} 
            customization={{
              paymentMethods: {
                creditCard: 'all',
                debitCard: 'all',
                ticket: 'all',
                bankTransfer: 'all',
              },
            }}
            onSubmit={handleCheckoutSubmit}
            onError={(error) => {
              console.error(error);
              setIsBrickLoading(false);
            }}
            onReady={() => {
              console.log('Payment Brick Ready!');
              setIsBrickLoading(false);
            }}
          />
          </div>
        </div>
      </div>
    </div>
  </div>,
  document.body
);
};

export default RetryPaymentModal;
